// LeaveForm.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../styles/LeavePage.css";
import NoirAlert from "../components/NoirAlert";
import { API_BASE_URL } from "../config";
import { useI18n } from "../providers/I18nProvider";
import { apiClient } from "../services/apiClient";
import { useAuth } from "../contexts/AuthContext";

const toLocalDateKey = (dateObj) => {
  if (!(dateObj instanceof Date) || Number.isNaN(dateObj.getTime())) return null;
  const y = dateObj.getFullYear();
  const m = String(dateObj.getMonth() + 1).padStart(2, "0");
  const d = String(dateObj.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const normalizeDateKey = (value) => {
  if (!value) return null;
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    return value.slice(0, 10);
  }
  const parsed = new Date(value);
  return toLocalDateKey(parsed);
};

const monthMatrix = (year, monthIndex0) => {
  const first = new Date(year, monthIndex0, 1);
  const last = new Date(year, monthIndex0 + 1, 0);
  const daysInMonth = last.getDate();
  const startDow = (first.getDay() + 6) % 7; // Monday=0

  const cells = [];
  for (let i = 0; i < startDow; i += 1) cells.push(null);
  for (let d = 1; d <= daysInMonth; d += 1) cells.push(new Date(year, monthIndex0, d));
  while (cells.length % 7 !== 0) cells.push(null);

  const weeks = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
  return weeks;
};

const holidayShortLabel = (name) => {
  if (!name) return "";
  const trimmed = String(name).trim();
  if (!trimmed) return "";
  const firstWord = trimmed.split(" ")[0];
  return firstWord.length > 3 ? `${firstWord.slice(0, 3)}…` : firstWord;
};

const LeaveForm = ({ onLeaveCreated, redirectTo = "/dashboard" }) => {
  const navigate = useNavigate();
  const { t, lang } = useI18n();
  const { user: authUser } = useAuth();
  const safeT = useCallback(
    (key, fallback) => {
      const value = t(key);
      return value === key ? fallback : value;
    },
    [t]
  );
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [selectedLeaveTypeId, setSelectedLeaveTypeId] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [annualDayPortion, setAnnualDayPortion] = useState("FULL"); // FULL | HALF (annual leave only)
  const [halfDayPeriod, setHalfDayPeriod] = useState("AM"); // AM | PM
  const [reason, setReason] = useState("");
  //const [error, setError] = useState("");
  const [notification, setNotification] = useState({ message: "", type: "" });
  const [currentUserData, setCurrentUserData] = useState(null);
  const [annualLeaveBalance, setAnnualLeaveBalance] = useState(0);
  const [userGender, setUserGender] = useState(null);
  const [loadingUserGender, setLoadingUserGender] = useState(true);
  const [publicHolidays, setPublicHolidays] = useState([]);
  const [step, setStep] = useState(0);
  const [calendarCursor, setCalendarCursor] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [calendarHolidays, setCalendarHolidays] = useState([]);
  const [minNegativeLeaveBalanceDays, setMinNegativeLeaveBalanceDays] = useState(-5);

  const holidayDateSet = useMemo(() => {
    const set = new Set();
    (publicHolidays || []).forEach((h) => {
      const key = normalizeDateKey(h?.date);
      if (key) set.add(key);
    });
    return set;
  }, [publicHolidays]);

  const calendarHolidayMap = useMemo(() => {
    const map = new Map();
    (calendarHolidays || []).forEach((h) => {
      const key = normalizeDateKey(h?.date);
      if (key) map.set(key, h?.name || "");
    });
    return map;
  }, [calendarHolidays]);

  const [subLeaveTypes, setSubLeaveTypes] = useState([]);
  const [selectedSubLeaveTypeId, setSelectedSubLeaveTypeId] = useState("");

  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [reportPdfFile, setReportPdfFile] = useState(null);

  // Görev/Seyahat: harcırah formu state'i
  const [travelTripType, setTravelTripType] = useState("domestic"); // domestic | international
  const [travelDestination, setTravelDestination] = useState("");
  const [travelPurpose, setTravelPurpose] = useState("");
  const [travelTransportRequired, setTravelTransportRequired] = useState(false);
  const [travelTransportType, setTravelTransportType] = useState(""); // plane|train|bus|car_rental
  const [travelAccommodationRequired, setTravelAccommodationRequired] = useState(false);
  const [travelAccommodationLink, setTravelAccommodationLink] = useState("");
  const [travelDailyAmountTry, setTravelDailyAmountTry] = useState(0);
  const [travelDailyAmountEur, setTravelDailyAmountEur] = useState(0);
  const [travelHasExtraExpenses, setTravelHasExtraExpenses] = useState(false);
  const [travelExpenseItems, setTravelExpenseItems] = useState([]); // [{payee_company, expense_date, invoice_amount, limit_amount, payable_amount, description, receiptFile}]

  const [timeSelectionWarning, setTimeSelectionWarning] = useState("");

  const selectedLeaveType = leaveTypes.find(
    (type) => type.id === parseInt(selectedLeaveTypeId)
  );
  const selectedSubLeaveType = subLeaveTypes.find(
    (subType) => subType.id === parseInt(selectedSubLeaveTypeId)
  );

  const selectedLeaveTypeNameLower = String(selectedLeaveType?.name || "")
    .trim()
    .toLocaleLowerCase("tr-TR");

  const isAnnualLeaveSelected = selectedLeaveTypeNameLower.startsWith("yıllık izin");

  const isExcuseLeaveSelected = selectedLeaveTypeNameLower === "mazeret izni";
  const isReportLeaveSelected = selectedLeaveTypeNameLower === "rapor";
  const isTravelLeaveSelected = selectedLeaveTypeNameLower === "görev/seyahat izni";

  const steps = useMemo(() => {
    const base = [
      { key: "type", label: t("leave.form.steps.type") },
      { key: "date", label: t("leave.form.steps.date") },
      { key: "reason", label: t("leave.form.steps.reason") },
    ];
    if (isTravelLeaveSelected) {
      base.push({ key: "travelDetail", label: safeT("leave.form.steps.travelDetail", "Seyahat") });
      base.push({ key: "harcirah", label: safeT("leave.form.steps.harcirah", "Harcırah") });
      base.push({ key: "ekHarcama", label: safeT("leave.form.steps.ekHarcama", "Ek Harcama") });
    }
    base.push({ key: "review", label: t("leave.form.steps.review") });
    return base;
  }, [t, safeT, isTravelLeaveSelected]);
  const currentStepKey = steps[step]?.key;

  const selectedSubLeaveTypeNameLower = String(selectedSubLeaveType?.name || "")
    .trim()
    .toLocaleLowerCase("tr-TR");

  const isSutIzniSelected = isExcuseLeaveSelected && selectedSubLeaveTypeNameLower === "süt izni";

  const toDateInputValue = (dateObj) => {
    if (!(dateObj instanceof Date) || Number.isNaN(dateObj.getTime())) return "";
    const y = dateObj.getFullYear();
    const m = String(dateObj.getMonth() + 1).padStart(2, "0");
    const d = String(dateObj.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };

  const getDefaultStartDate = () => {
    const base = new Date();
    const dow = base.getDay();
    if (dow === 6) {
      base.setDate(base.getDate() + 2);
    } else if (dow === 0) {
      base.setDate(base.getDate() + 1);
    }
    return base;
  };

  const addDays = (dateObj, days) => {
    const next = new Date(dateObj.getTime());
    next.setDate(next.getDate() + days);
    return next;
  };

  // Varsayılan olarak uygun baslangic ve bitis tarihlerini doldur
  useEffect(() => {
    const start = getDefaultStartDate();
    const end = addDays(start, 1);
    setStartDate((prev) => prev || toDateInputValue(start));
    setEndDate((prev) => prev || toDateInputValue(end));
    setStartTime((prev) => prev || "09:00");
    setEndTime((prev) => prev || "18:00");
  }, []);

  // Saatlik izin kontrolü: is_duration_flexible, "Saatlik İzin" veya süt izni
  const isHourlyLeaveSelected =
    selectedLeaveType?.is_duration_flexible || isSutIzniSelected;

  const isHalfDaySelected =
    isAnnualLeaveSelected && !isHourlyLeaveSelected && annualDayPortion === "HALF";

  useEffect(() => {
    if (isHourlyLeaveSelected || isHalfDaySelected) {
      if (startDate) setEndDate(startDate);
    }
  }, [isHourlyLeaveSelected, isHalfDaySelected, startDate]);

  const holidayRangeStart = startDate ? String(startDate).slice(0, 10) : null;
  const holidayRangeEnd = isHourlyLeaveSelected
    ? holidayRangeStart
    : endDate
      ? String(endDate).slice(0, 10)
      : holidayRangeStart;

  useEffect(() => {
    const loadPublicHolidays = async () => {
      if (!holidayRangeStart || !holidayRangeEnd) {
        setPublicHolidays([]);
        return;
      }
      try {
        const res = await apiClient.get("/public-holidays", {
          params: { start: holidayRangeStart, end: holidayRangeEnd },
        });
        setPublicHolidays(res.data || []);
      } catch (err) {
        setPublicHolidays([]);
      }
    };

    loadPublicHolidays();
  }, [holidayRangeStart, holidayRangeEnd]);

  useEffect(() => {
    const loadCalendarHolidays = async () => {
      const start = new Date(calendarCursor.getFullYear(), calendarCursor.getMonth(), 1);
      const end = new Date(calendarCursor.getFullYear(), calendarCursor.getMonth() + 2, 0);
      const startKey = toLocalDateKey(start);
      const endKey = toLocalDateKey(end);
      if (!startKey || !endKey) {
        setCalendarHolidays([]);
        return;
      }

      try {
        const res = await apiClient.get("/public-holidays", {
          params: { start: startKey, end: endKey },
        });
        setCalendarHolidays(res.data || []);
      } catch {
        setCalendarHolidays([]);
      }
    };

    loadCalendarHolidays();
  }, [calendarCursor]);

  // Kullanicinin cinsiyetini AuthContext'ten al.
  useEffect(() => {
    if (authUser) {
      setUserGender(authUser.gender ?? null);
    }
    setLoadingUserGender(false);
  }, [authUser]);

  useEffect(() => {
    apiClient
      .get("/site-settings")
      .then((res) => {
        const v = Number(res.data?.min_negative_leave_balance_days);
        setMinNegativeLeaveBalanceDays(Number.isFinite(v) ? v : -5);
        const tt = Number(res.data?.travel_daily_amount_try);
        const te = Number(res.data?.travel_daily_amount_eur);
        setTravelDailyAmountTry(Number.isFinite(tt) ? tt : 0);
        setTravelDailyAmountEur(Number.isFinite(te) ? te : 0);
      })
      .catch(() => {
        setMinNegativeLeaveBalanceDays(-5);
      });
  }, []);

  // Veri çekme için yardımcı fonksiyon
  const fetchData = useCallback(async (url) => {
    const response = await fetch(url, {
      method: "GET",
      credentials: "include",
    });

    if (!response.ok) {
      const errorText = await response.text();
      let errorMessage = errorText;
      try {
        const errorData = JSON.parse(errorText);
        errorMessage = errorData.msg || errorData.error || errorText;
      } catch (e) {
        // JSON hatası değilse
      }
      throw new Error(`Veri alınırken hata oluştu: ${errorMessage}`);
    }
    return response.json();
  }, []);

  // İşe dönüş tarihini hesaplayan yardımcı fonksiyon
  const calculateReturnToWorkDate = (leaveEndDateStr) => {
    if (!leaveEndDateStr) return null;

    let returnDate = new Date(leaveEndDateStr);
    returnDate.setDate(returnDate.getDate() + 1);

    while (returnDate.getDay() === 0 || returnDate.getDay() === 6) {
      // 0: Pazar, 6: Cumartesi
      returnDate.setDate(returnDate.getDate() + 1);
    }
    return returnDate;
  };

  // Belirli bir başlangıç tarihinden itibaren X iş günü sonrası bitiş tarihini hesaplar
  const calculateEndDateBasedOnBusinessDays = (startDateStr, businessDays) => {
    if (
      !startDateStr ||
      businessDays === null ||
      businessDays === undefined ||
      businessDays <= 0
    )
      return null;

    let current = new Date(startDateStr);
    let daysCounted = 0;

    while (daysCounted < businessDays) {
      const dayOfWeek = current.getDay();
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        // Pazar (0) ve Cumartesi (6) değilse
        daysCounted++;
      }
      if (daysCounted < businessDays) {
        // Eğer henüz hedef güne ulaşmadıysak bir sonraki güne geç
        current.setDate(current.getDate() + 1);
      }
    }
    return current.toISOString().split("T")[0];
  };

  const calculateTotalLeaveHours = () => {
    // Süt izni için süre, başlangıç ve bitiş saatine göre hesaplanır
    if (isSutIzniSelected) {
      if (!startDate || !startTime || !endTime) return 0;
      const startDateTime = new Date(`${startDate}T${startTime}`);
      const endDateTime = new Date(`${startDate}T${endTime}`);
      if (startDateTime >= endDateTime) return 0;
      let diffMilliseconds = endDateTime.getTime() - startDateTime.getTime();
      return diffMilliseconds / (1000 * 60 * 60);
    }
    // Diğer tüm saatlik izinler için öğle arası düşülerek hesaplanır
    if (!startDate || !startTime || !endTime) return 0;

    const startDateTime = new Date(`${startDate}T${startTime}`);
    const endDateTime = new Date(`${startDate}T${endTime}`);

    if (isNaN(startDateTime.getTime()) || isNaN(endDateTime.getTime()))
      return 0;

    if (startDateTime >= endDateTime) return 0;

    let diffMilliseconds = endDateTime.getTime() - startDateTime.getTime();

    const lunchBreakStart = new Date(`${startDate}T12:30`);
    const lunchBreakEnd = new Date(`${startDate}T13:30`);
    const isOverlappingLunch =
      startDateTime < lunchBreakEnd && endDateTime > lunchBreakStart;

    if (isOverlappingLunch) {
      const effectiveOverlapStart = Math.max(
        startDateTime.getTime(),
        lunchBreakStart.getTime()
      );
      const effectiveOverlapEnd = Math.min(
        endDateTime.getTime(),
        lunchBreakEnd.getTime()
      );
      if (effectiveOverlapEnd > effectiveOverlapStart) {
        diffMilliseconds -= effectiveOverlapEnd - effectiveOverlapStart;
      }
    }

    return diffMilliseconds / (1000 * 60 * 60); // saat
  };

  // İşe dönüş saatini hesaplar (Süt izni için) - Şimdi genel saatlik izinler için de kullanılacak
  const calculateReturnToWorkTime = () => {
    if (!startDate || !endTime) return null;

    // Öğle arası (12:30 - 13:30)
    const lunchBreakStart = new Date(`${startDate}T12:30`);
    const lunchBreakEnd = new Date(`${startDate}T13:30`);
    const endDateTime = new Date(`${startDate}T${endTime}`);

    // Eğer bitiş saati öğle arasından önceyse veya öğle arasından sonraysa direkt bitiş saatini döndür.
    // Eğer bitiş saati öğle arasında kalıyorsa, öğle arasının bitiş saatini döndür.
    if (endDateTime <= lunchBreakStart) {
      return endTime;
    } else if (endDateTime > lunchBreakStart && endDateTime <= lunchBreakEnd) {
      return lunchBreakEnd.toLocaleTimeString("tr-TR", {
        hour: "2-digit",
        minute: "2-digit",
      });
    } else {
      return endTime;
    }
  };

  // Saatleri HH:MM formatına dönüştüren yardımcı fonksiyon
  const formatHoursToHHMM = (totalHours) => {
    if (totalHours === null || totalHours === undefined) return "";
    const hours = Math.floor(totalHours);
    const minutes = Math.round((totalHours - hours) * 60);
    return `${hours}:${minutes.toString().padStart(2, "0")} saat`;
  };

  // İlk yüklemede izin türlerini ve kullanıcı verilerini çek
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const types = await fetchData(`${API_BASE_URL}/leave-types`);
        setLeaveTypes(types);

        const userData = await fetchData(`${API_BASE_URL}/users/me`);
        setCurrentUserData(userData);
      } catch (err) {
        console.error("Veri çekilirken hata:", err);
        //setError(err.message || "Gerekli veriler yüklenirken bir hata oluştu.");
        setNotification({
          message: t("leave.form.errors.loadFailed"),
          type: "error",
        });
      }
    };
    loadInitialData();
  }, [fetchData, t]);

  // Kullanıcı verileri değiştiğinde yıllık izin bakiyesini güncelle
  useEffect(() => {
    setAnnualLeaveBalance(Number(currentUserData?.total_leave_quota_days || 0));
  }, [currentUserData]);

  // Seçilen izin türü değiştiğinde alt türleri getir, tarih ve saatleri sıfırla
  useEffect(() => {
    setStartDate("");
    setEndDate("");
    setAnnualDayPortion("FULL");
    setHalfDayPeriod("AM");
    setSelectedSubLeaveTypeId("");
    setSubLeaveTypes([]);
    setStartTime("");
    setEndTime("");
    setReportPdfFile(null);
    setTimeSelectionWarning(""); // İzin türü değiştiğinde uyarıyı sıfırla

    if (loadingUserGender) {
      return;
    }

    if (selectedLeaveType) {
      if (isExcuseLeaveSelected) {
        const fetchSubTypes = async () => {
          try {
            const subTypes = await fetchData(
              `${API_BASE_URL}/leave-sub-types/${selectedLeaveType.id}`
            );

            const filteredSubTypes = subTypes.filter((subType) => {
              const subTypeNameLower = subType.name.toLocaleLowerCase("tr-TR");

              if (
                userGender === "E" &&
                (subTypeNameLower === "doğum izni" ||
                  subTypeNameLower === "süt izni")
              ) {
                return false;
              }
              if (userGender === "K" && subTypeNameLower === "babalık izni") {
                return false;
              }
              return true;
            });

            setSubLeaveTypes(filteredSubTypes);
          } catch (err) {
            console.error("Alt izin türleri çekilirken hata:", err);
            //setError(err.message || "Alt izin türleri yüklenirken bir hata oluştu.");
            setNotification({
              message: "Alt izin türleri yüklenirken bir hata oluştu.",
              type: "error",
            });
          }
        };
        fetchSubTypes();
      } else {
        // Eğer izin türü esnek süreye sahip değilse ve varsayılan süresi varsa, başlangıç ve bitiş tarihlerini ayarla
        if (
          !selectedLeaveType.is_duration_flexible &&
          selectedLeaveType.default_duration_days
        ) {
          const today = new Date().toISOString().split("T")[0];
          setStartDate(today);
          const calculatedEndDate = calculateEndDateBasedOnBusinessDays(
            today,
            selectedLeaveType.default_duration_days
          );
          setEndDate(calculatedEndDate);
        }
      }
    } else {
      // no-op
    }
  }, [selectedLeaveTypeId, selectedLeaveType, userGender, loadingUserGender, isExcuseLeaveSelected, fetchData]);

  // Seçilen alt izin türü (mazeret izni) değiştiğinde varsayılan tarih ve saatleri doldurma
  useEffect(() => {
    if (isExcuseLeaveSelected && selectedSubLeaveType) {
      const today = new Date().toISOString().split("T")[0];
      setStartDate(today);

      if (selectedSubLeaveType.name.toLocaleLowerCase("tr-TR") === "süt izni") {
        // Süt İzni için varsayılan saatleri ayarlayabilirsiniz
        setStartTime("09:00");
        setEndTime("10:30"); // 1.5 saat
      } else if (selectedSubLeaveType.default_duration_days !== undefined) {
        const calculatedEndDate = calculateEndDateBasedOnBusinessDays(
          today,
          selectedSubLeaveType.default_duration_days
        );
        setEndDate(calculatedEndDate);
      }
    }
  }, [selectedSubLeaveType, isExcuseLeaveSelected]);

  // Saatlik izin seçildiğinde bitiş tarihini başlangıç tarihiyle aynı yap
  useEffect(() => {
    //setError(""); // İlgili alanlar değiştiğinde hataları temizle
    setNotification({ message: "", type: "" });
    setTimeSelectionWarning(""); // Saat uyarılarını temizle

    if (isHourlyLeaveSelected && startDate) {
      // Saatlik izin ise, bitiş tarihini başlangıç tarihiyle aynı yap
      setEndDate(startDate);
    }
  }, [startDate, isHourlyLeaveSelected]); // Bağımlılıklar güncellendi

  // Bitiş tarihi için maksimum sınırı hesapla
  const calculateMaxEndDate = () => {
    if (!startDate || !selectedLeaveType) return "";

    let maxDaysLimit = null;

    if (isAnnualLeaveSelected) {
      const allowedNegative = Math.max(0, -Number(minNegativeLeaveBalanceDays || 0));
      maxDaysLimit = annualLeaveBalance + allowedNegative;

      if (maxDaysLimit < 0) {
        maxDaysLimit = 0;
      } else if (maxDaysLimit === 0) {
        maxDaysLimit = 1;
      }
    } else if (
      isExcuseLeaveSelected &&
      selectedSubLeaveType &&
      selectedSubLeaveType.max_duration_days !== undefined
    ) {
      maxDaysLimit = selectedSubLeaveType.max_duration_days;
    } else if (
      selectedLeaveType &&
      selectedLeaveType.max_duration_days !== undefined
    ) {
      maxDaysLimit = selectedLeaveType.max_duration_days;
    }

    if (
      maxDaysLimit !== null &&
      maxDaysLimit !== undefined &&
      maxDaysLimit > 0
    ) {
      return calculateEndDateBasedOnBusinessDays(startDate, maxDaysLimit);
    }
    return "";
  };

  // Toplam izin süresini iş günü olarak hesapla (saatlik izin için bu kullanılmayacak)
  const calculateTotalLeaveDays = () => {
    if (!startDate || !endDate) return 0;

    // Eğer seçili izin türü saatlik ise, gün bazında hesaplama yapma
    if (isHourlyLeaveSelected) {
      return 0; // Gün bazında 0 döndür
    }

    // Görev/Seyahat izni: hafta sonları dahil, her iki uç günü de sayan
    // takvim günü hesabı (ör. 13 → 19 = 7 gün).
    if (isTravelLeaveSelected) {
      const start = new Date(`${startDate}T00:00:00`);
      const end = new Date(`${endDate}T00:00:00`);
      if (
        Number.isNaN(start.getTime()) ||
        Number.isNaN(end.getTime()) ||
        start > end
      )
        return 0;
      const MS_PER_DAY = 24 * 60 * 60 * 1000;
      return Math.round((end.getTime() - start.getTime()) / MS_PER_DAY) + 1;
    }

    if (isHalfDaySelected) {
      const d = new Date(`${startDate}T00:00:00`);
      if (Number.isNaN(d.getTime())) return 0;
      const dayOfWeek = d.getDay();
      const key = toLocalDateKey(d);
      const isHoliday = key ? holidayDateSet.has(key) : false;
      if (dayOfWeek === 0 || dayOfWeek === 6 || isHoliday) return 0;
      return 0.5;
    }

    const start = new Date(startDate);
    const end = new Date(endDate);
    if (start > end) return 0;

    let totalDays = 0;
    let current = new Date(start);
    while (current <= end) {
      const dayOfWeek = current.getDay();
      const key = toLocalDateKey(current);
      const isHoliday = key ? holidayDateSet.has(key) : false;
      if (dayOfWeek !== 0 && dayOfWeek !== 6 && !isHoliday) {
        // 0: Pazar, 6: Cumartesi (hafta sonlarını atla)
        totalDays++;
      }
      current.setDate(current.getDate() + 1);
    }
    return totalDays;
  };

  const totalCalculatedDays = calculateTotalLeaveDays();
  const totalCalculatedHours = calculateTotalLeaveHours(); // Yeni: Toplam saat

  let currentBalance = 0;
  if (isAnnualLeaveSelected && !isHourlyLeaveSelected) {
    currentBalance = annualLeaveBalance;
  }

  const remainingBalanceAfterLeave = currentBalance - totalCalculatedDays; // Yıllık izin için hala gün bazında

  const returnToWorkDate = useMemo(() => {
    if (isHalfDaySelected) {
      if (!startDate) return null;
      if (halfDayPeriod === "AM") return new Date(`${startDate}T00:00:00`);
      return calculateReturnToWorkDate(startDate);
    }
    return calculateReturnToWorkDate(endDate);
  }, [isHalfDaySelected, halfDayPeriod, startDate, endDate]);
  const returnToWorkTime = calculateReturnToWorkTime(); // Yeni: İşe dönüş saati

  // Saat seçimleri değiştiğinde anlık uyarıları kontrol et
  useEffect(() => {
    // Sadece saatlik izinler için bu kontrolü çalıştır
    if (isHourlyLeaveSelected && startDate && startTime && endTime) {
      const start = new Date(`${startDate}T${startTime}`);
      const end = new Date(`${startDate}T${endTime}`);
      const workStart = new Date(`${startDate}T08:00`);
      const workEnd = new Date(`${startDate}T18:00`);

      let warning = "";

      if (start >= end) {
        warning = t("leave.form.warnings.timeRangeInvalid");
      } else if (start < workStart) {
        warning = t("leave.form.warnings.startTimeMin");
      } else if (end > workEnd) {
        warning = t("leave.form.warnings.endTimeMax");
      } else if (totalCalculatedHours <= 0) {
        warning = t("leave.form.warnings.timeRangeInvalid");
      } else {
        // --- İzin türüne göre maksimum süreleri belirle ---
        let maxDurationHours;

        if (isSutIzniSelected) {
          maxDurationHours = 1.5; // Süt İzni için 1.5 saat
        } else if (
          selectedLeaveType?.is_duration_flexible
        ) {
          maxDurationHours = 4.5; // Genel Saatlik İzin için 4.5 saat
        } else {
          maxDurationHours = null; // Diğer izin türleri için saat kontrolü yok
        }
        // --- Maksimum süre belirleme sonu ---

        if (
          maxDurationHours !== null &&
          totalCalculatedHours > maxDurationHours
        ) {
          warning = t("leave.form.warnings.maxDuration", {
            max: formatHoursToHHMM(maxDurationHours),
            selected: formatHoursToHHMM(totalCalculatedHours),
          });
        }
      }

      if (warning) {
        setNotification({ message: warning, type: "warning" });
      } else {
        setNotification({ message: "", type: "" });
      }
    } else {
      // Saat seçimi kaldırıldığında veya uygun koşul sağlanmadığında bildirimi temizle.
      setNotification({ message: "", type: "" });
    }
  }, [
    startDate,
    startTime,
    endTime,
    isHourlyLeaveSelected,
    isSutIzniSelected,
    selectedSubLeaveType,
    selectedLeaveType,
    totalCalculatedHours,
    t,
  ]);

  // Çift tıklama / Enter ile mükerrer talep oluşmasın
  const submittingRef = React.useRef(false);
  const [submitting, setSubmitting] = useState(false);
  const handleSubmit = async (e) => {
    e.preventDefault();
    // Ara adımda Enter'a basmak formu göndermesin, sadece sonraki adıma geçsin
    if (step < steps.length - 1) {
      handleNext();
      return;
    }
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    try {
      await submitLeave();
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const submitLeave = async () => {
    //setError("");
    setNotification({ message: "", type: "" });

    if (!selectedLeaveTypeId || !startDate || !reason) {
      setNotification({
        message: t("leave.form.errors.requiredFields"),
        type: "error",
      });
      return;
    }

    if (isExcuseLeaveSelected && !selectedSubLeaveTypeId) {
      setNotification({
        message: t("leave.form.errors.subTypeRequired"),
        type: "error",
      });
      return;
    }

    if (isHourlyLeaveSelected) {
      if (timeSelectionWarning) {
        //setError(timeSelectionWarning);
        setNotification({ message: timeSelectionWarning, type: "error" });
        return;
      }

      if (!startTime || !endTime) {
        setNotification({
          message: t("leave.form.errors.timeRequired"),
          type: "error",
        });
        return;
      }

      let maxDurationHours = selectedLeaveType?.max_duration_hours;
      if (isExcuseLeaveSelected && selectedSubLeaveType) {
        maxDurationHours =
          selectedSubLeaveType.max_duration_hours || maxDurationHours;
      }
      if (maxDurationHours === null || maxDurationHours === undefined) {
        maxDurationHours =
          isSutIzniSelected
            ? 1.5
            : selectedLeaveType?.is_duration_flexible
            ? 8
            : null;
      }

      if (
        totalCalculatedHours === 0 ||
        (maxDurationHours !== null && totalCalculatedHours > maxDurationHours)
      ) {
        setNotification({
          message: t("leave.form.errors.hourlyLimit", {
            max: formatHoursToHHMM(maxDurationHours),
          }),
          type: "error",
        });
        return;
      }
    } else {
      // Günlük izinler için bitiş tarihi kontrolü
      if (!isHalfDaySelected && !endDate) {
        setNotification({ message: t("leave.form.errors.endDateRequired"), type: "error" });
        return;
      }
    }

    const leaveRequestData = {
      leave_type_id: parseInt(selectedLeaveTypeId, 10),
      leave_sub_type_id:
        isExcuseLeaveSelected && selectedSubLeaveTypeId
          ? parseInt(selectedSubLeaveTypeId, 10)
          : null,
      start_date: startDate,
      end_date: isHourlyLeaveSelected || isHalfDaySelected ? startDate : endDate,
      half_day_period: isHalfDaySelected ? halfDayPeriod : null,
      reason: reason,
      calculated_days:
        isHourlyLeaveSelected
          ? 0
          : totalCalculatedDays,
      ...(isHourlyLeaveSelected
        ? {
            total_duration_hours:
              totalCalculatedHours,
            start_time: startTime,
            end_time: endTime,
          }
        : {}),
      ...(isTravelLeaveSelected
        ? {
            travel_expense: {
              destination: travelDestination.trim(),
              purpose: travelPurpose.trim() || null,
              trip_type: travelTripType,
              transport_required: travelTransportRequired,
              transport_type: travelTransportRequired ? travelTransportType : null,
              accommodation_required: travelAccommodationRequired,
              accommodation_link: travelAccommodationRequired
                ? travelAccommodationLink.trim()
                : null,
              has_extra_expenses: travelHasExtraExpenses,
              items: travelHasExtraExpenses
                ? travelExpenseItems.map((it) => ({
                    payee_company: String(it.payee_company || "").trim(),
                    expense_date: it.expense_date || null,
                    invoice_amount: Number(it.invoice_amount || 0),
                    limit_amount:
                      it.limit_amount === "" || it.limit_amount === null || it.limit_amount === undefined
                        ? null
                        : Number(it.limit_amount),
                    payable_amount: Number(it.payable_amount || 0),
                    description: String(it.description || "").trim() || null,
                  }))
                : [],
            },
          }
        : {}),
    };

    try {
      const response = await apiClient.post("/leaves", leaveRequestData);

      if (isReportLeaveSelected && reportPdfFile) {
        try {
          const formData = new FormData();
          formData.append("file", reportPdfFile);
          await apiClient.post(`/leaves/${response.data.id}/report-pdf`, formData);
        } catch (uploadErr) {
          setNotification({
            message:
              uploadErr.response?.data?.msg ||
              uploadErr.message ||
              safeT("leave.form.reportPdfUploadFailed", "Rapor PDF yüklenemedi."),
            type: "warning",
          });
        }
      }

      if (
        isTravelLeaveSelected &&
        travelHasExtraExpenses &&
        Array.isArray(response.data?.travel_expense?.items)
      ) {
        const itemIds = response.data.travel_expense.items.map((it) => it.id);
        for (let i = 0; i < travelExpenseItems.length; i += 1) {
          const file = travelExpenseItems[i]?.receiptFile;
          const itemId = itemIds[i];
          if (!file || !itemId) continue;
          try {
            const formData = new FormData();
            formData.append("file", file);
            await apiClient.post(
              `/leaves/${response.data.id}/travel-expense-items/${itemId}/receipt-pdf`,
              formData
            );
          } catch (uploadErr) {
            setNotification({
              message:
                uploadErr.response?.data?.msg ||
                uploadErr.message ||
                safeT("leave.form.travel.receiptUploadFailed", "Bir fiş PDF yüklenemedi."),
              type: "warning",
            });
          }
        }
      }

      const warningMsg =
        Array.isArray(response.data?.warnings) && response.data.warnings[0]?.message
          ? response.data.warnings[0].message
          : "";
      if (warningMsg) {
        setNotification({ message: warningMsg, type: "warning" });
      }

      onLeaveCreated(response.data);
      if (redirectTo) navigate(redirectTo);
    } catch (err) {
      console.error("İzin talebi gönderilirken hata:", err);
      //setError(err.response?.data?.msg || err.message || "Bir hata oluştu.");
      setNotification({
        message: err.response?.data?.msg || err.message || t("common.genericError"),
        type: "error",
      });
    }
  };

  const validateStep = (currentStep) => {
    const key = steps[currentStep]?.key;
    if (key === "type") {
      if (!selectedLeaveTypeId) {
        setNotification({
          message: t("leave.form.errors.requiredFields"),
          type: "error",
        });
        return false;
      }
      if (isExcuseLeaveSelected && subLeaveTypes.length > 0 && !selectedSubLeaveTypeId) {
        setNotification({
          message: t("leave.form.errors.subTypeRequired"),
          type: "error",
        });
        return false;
      }
      return true;
    }

    if (key === "date") {
      if (!startDate) {
        setNotification({
          message: t("leave.form.errors.requiredFields"),
          type: "error",
        });
        return false;
      }
      if (isHourlyLeaveSelected) {
        if (timeSelectionWarning) {
          setNotification({ message: timeSelectionWarning, type: "error" });
          return false;
        }
        if (!startTime || !endTime || totalCalculatedHours <= 0) {
          setNotification({
            message: t("leave.form.errors.timeRequired"),
            type: "error",
          });
          return false;
        }
      } else if (isHalfDaySelected) {
        if (!halfDayPeriod) {
          setNotification({
            message: safeT("leave.form.halfDayPeriodRequired", "Yarım gün seçimi zorunlu."),
            type: "error",
          });
          return false;
        }
      } else if (!endDate) {
        setNotification({ message: t("leave.form.errors.endDateRequired"), type: "error" });
        return false;
      }
      return true;
    }

    if (key === "reason") {
      if (!reason || !reason.trim()) {
        setNotification({
          message: t("leave.form.errors.requiredFields"),
          type: "error",
        });
        return false;
      }
      return true;
    }

    if (key === "travelDetail") {
      if (!travelDestination.trim()) {
        setNotification({
          message: safeT(
            "leave.form.travel.errors.destinationRequired",
            "Seyahat edilen yer zorunludur."
          ),
          type: "error",
        });
        return false;
      }
      if (travelTransportRequired && !travelTransportType) {
        setNotification({
          message: safeT(
            "leave.form.travel.errors.transportTypeRequired",
            "Ulaşım tipi seçilmelidir."
          ),
          type: "error",
        });
        return false;
      }
      if (travelAccommodationRequired) {
        const link = travelAccommodationLink.trim();
        if (!link) {
          setNotification({
            message: safeT(
              "leave.form.travel.errors.accommodationLinkRequired",
              "Konaklama için otel linki giriniz."
            ),
            type: "error",
          });
          return false;
        }
        if (!/^https?:\/\//i.test(link)) {
          setNotification({
            message: safeT(
              "leave.form.travel.errors.accommodationLinkInvalid",
              "Konaklama linki http(s):// ile başlamalı."
            ),
            type: "error",
          });
          return false;
        }
      }
      return true;
    }

    if (key === "harcirah") {
      if (!(totalCalculatedDays > 0)) {
        setNotification({
          message: safeT(
            "leave.form.travel.errors.daysRequired",
            "Harcırah için iş günü sayısı pozitif olmalı."
          ),
          type: "error",
        });
        return false;
      }
      return true;
    }

    if (key === "ekHarcama") {
      if (!travelHasExtraExpenses) return true;
      if (travelExpenseItems.length === 0) {
        setNotification({
          message: safeT(
            "leave.form.travel.errors.itemRequired",
            "Ek harcama formunda en az bir kalem ekleyin veya 'Ek harcama yok' seçin."
          ),
          type: "error",
        });
        return false;
      }
      for (let i = 0; i < travelExpenseItems.length; i += 1) {
        const it = travelExpenseItems[i];
        if (!String(it.payee_company || "").trim()) {
          setNotification({
            message: `${safeT(
              "leave.form.travel.errors.itemPayeeRequired",
              "Ek harcama kalemi"
            )} #${i + 1}: ${safeT(
              "leave.form.travel.errors.payeeRequired",
              "Ödeme yapılan şirket zorunlu."
            )}`,
            type: "error",
          });
          return false;
        }
        if (it.expense_date && !/^\d{4}-\d{2}-\d{2}$/.test(it.expense_date)) {
          setNotification({
            message: `Kalem #${i + 1}: ${safeT(
              "leave.form.travel.errors.invalidDate",
              "Tarih geçerli değil."
            )}`,
            type: "error",
          });
          return false;
        }
        const invoice = Number(it.invoice_amount);
        const payable = Number(it.payable_amount);
        if (!(Number.isFinite(invoice) && invoice >= 0)) {
          setNotification({
            message: `Kalem #${i + 1}: ${safeT(
              "leave.form.travel.errors.invalidInvoice",
              "Fatura bedeli geçerli değil."
            )}`,
            type: "error",
          });
          return false;
        }
        if (!(Number.isFinite(payable) && payable >= 0)) {
          setNotification({
            message: `Kalem #${i + 1}: ${safeT(
              "leave.form.travel.errors.invalidPayable",
              "Ödenecek bedel geçerli değil."
            )}`,
            type: "error",
          });
          return false;
        }
      }
      return true;
    }

    return true;
  };

  const handleNext = () => {
    if (!validateStep(step)) return;
    setStep((prev) => Math.min(prev + 1, steps.length - 1));
  };

  const handleBack = () => {
    setStep((prev) => Math.max(prev - 1, 0));
  };

  // showLeaveSummary koşulunu güncelledik
  const showLeaveSummary =
    selectedLeaveType &&
    (isAnnualLeaveSelected ||
      selectedLeaveType.name === "Ücretsiz İzin" ||
      selectedLeaveType.name === "Görev/Seyahat İzni" ||
      isHourlyLeaveSelected ||
      (isExcuseLeaveSelected && selectedSubLeaveTypeId));

  const leaveSummaryTitle =
    isAnnualLeaveSelected
      ? t("leave.summary.annualTitle")
      : selectedLeaveType?.name === "Ücretsiz İzin"
      ? t("leave.summary.unpaidTitle")
      : selectedLeaveType?.name === "Görev/Seyahat İzni"
      ? t("leave.summary.travelTitle")
      : isHourlyLeaveSelected
      ? t("leave.summary.hourlyTitle")
      : selectedLeaveType?.name === "Mazeret İzni"
      ? t("leave.summary.excuseTitle")
      : t("leave.summary.title");
  const shouldShowSummary = showLeaveSummary && step >= 2;
  const minStartKey = useMemo(() => {
    const daysPastAllowed = 15;
    const d = new Date();
    d.setDate(d.getDate() - daysPastAllowed);
    return toLocalDateKey(d);
  }, []);

  return (
    <form className="leave-form wizard" onSubmit={handleSubmit}>
      <NoirAlert
        message={notification.message}
        type={notification.type}
        onClose={() => setNotification({ message: "", type: "" })}
      />

      <div className="wizard-steps">
        {steps.map((s, idx) => (
          <div
            key={s.key}
            className={`wizard-step ${idx === step ? "active" : ""} ${
              idx < step ? "done" : ""
            }`}
          >
            <span className="step-index">{idx + 1}</span>
            <span className="step-label">{s.label}</span>
          </div>
        ))}
      </div>

      {currentStepKey === "type" && (
        <div className="step-panel">
          <div>
            <label>{t("leave.form.leaveType")}:</label>
            <select
              className="leave-form select"
              value={selectedLeaveTypeId}
              onChange={(e) => setSelectedLeaveTypeId(e.target.value)}
              required
            >
              {loadingUserGender || leaveTypes.length === 0 ? (
                <option value="">{t("common.loading")}</option>
              ) : (
                <>
                  <option value="">{t("leave.form.leaveTypeSelect")}</option>
                  {leaveTypes.map((type) => {
                    const typeNameLower = type.name.toLocaleLowerCase("tr-TR");
                    const isAnnualType = typeNameLower.startsWith("yıllık izin");
                    if (isAnnualType) {
                      const years =
                        Number.isFinite(Number(currentUserData?.seniority_years))
                          ? Number(currentUserData.seniority_years)
                          : null;
                      if (years != null) {
                        const wantUpper = years >= 5;
                        const isUpper = typeNameLower.includes("ve üstü");
                        const isLower = typeNameLower.includes("altı");
                        if (wantUpper && !isUpper) return null;
                        if (!wantUpper && !isLower) return null;
                      }
                    }

                    if (
                      userGender === "E" &&
                      (typeNameLower === "doğum izni" ||
                        typeNameLower === "süt izni")
                    ) {
                      return null;
                    }
                    if (userGender === "K" && typeNameLower === "babalık izni") {
                      return null;
                    }

                    return (
                      <option key={type.id} value={type.id}>
                        {type.name}
                      </option>
                    );
                  })}
                </>
              )}
            </select>
          </div>

          {selectedLeaveType &&
            isExcuseLeaveSelected &&
            subLeaveTypes.length > 0 && (
              <div>
                <label>{t("leave.form.subType")}:</label>
                <select
                  className="leave-form select"
                  value={selectedSubLeaveTypeId}
                  onChange={(e) => setSelectedSubLeaveTypeId(e.target.value)}
                  required={isExcuseLeaveSelected && !isHourlyLeaveSelected}
                >
                  <option value="">{t("leave.form.select")}</option>
                  {subLeaveTypes.map((subType) => (
                    <option key={subType.id} value={subType.id}>
                      {subType.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
        </div>
      )}

      {currentStepKey === "date" && (
        <div className="step-panel two-col">
          <div>
            <label>{t("leave.form.startDate")}:</label>
            <input
              className="leave-form input"
              type="date"
              value={startDate}
              onChange={(e) => {
                const next = e.target.value;
                setStartDate(next);
                if (isHourlyLeaveSelected || isHalfDaySelected) setEndDate(next);
              }}
              min={minStartKey}
              required
            />
          </div>

          {!isHourlyLeaveSelected && isAnnualLeaveSelected && (
            <div>
              <label>{safeT("leave.form.dayPortion", "Gün seçimi")}:</label>
              <select
                className="leave-form select"
                value={annualDayPortion}
                onChange={(e) => setAnnualDayPortion(e.target.value)}
              >
                <option value="FULL">{safeT("leave.form.dayPortion.full", "Tam gün")}</option>
                <option value="HALF">{safeT("leave.form.dayPortion.half", "Yarım gün")}</option>
              </select>
            </div>
          )}

          {isHourlyLeaveSelected ? (
            <>
              <div>
                <label>{t("leave.form.startTime")}:</label>
                <input
                  className="leave-form input"
                  type="time"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  min="08:00"
                  max="18:00"
                  step="600"
                  required
                />
              </div>
              <div>
                <label>{t("leave.form.endTime")}:</label>
                <input
                  className="leave-form input"
                  type="time"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  min={startTime}
                  max="18:00"
                  step="600"
                  required
                />
              </div>
              {timeSelectionWarning && (
                <p className="warning-text">{timeSelectionWarning}</p>
              )}
              <div style={{ display: "none" }}>
                <label>{t("leave.form.endDate")}:</label>
                <input type="date" value={endDate} readOnly />
              </div>
            </>
          ) : isHalfDaySelected ? (
            <div>
              <label>{safeT("leave.form.halfDayPeriod", "Yarım gün")}:</label>
              <select
                className="leave-form select"
                value={halfDayPeriod}
                onChange={(e) => setHalfDayPeriod(e.target.value)}
                required
              >
                <option value="AM">{safeT("leave.form.halfDayPeriod.am", "Öğleden önce")}</option>
                <option value="PM">{safeT("leave.form.halfDayPeriod.pm", "Öğleden sonra")}</option>
              </select>
            </div>
          ) : (
            <div>
              <label>{t("leave.form.endDate")}:</label>
              <input
                className="leave-form input"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                min={startDate || minStartKey}
                max={calculateMaxEndDate()}
                required
              />
            </div>
          )}

          <div className="range-calendar" aria-label={safeT("leave.form.calendarAria", "Tarih seçimi")}>
            <div className="range-calendar-head">
              <div className="range-calendar-title">
                {safeT("leave.form.calendarTitle", "Takvimden seç")}
              </div>
              <div className="range-calendar-nav">
                <button
                  className="ghost"
                  type="button"
                  onClick={() => {
                    const d = new Date(
                      calendarCursor.getFullYear(),
                      calendarCursor.getMonth() - 1,
                      1
                    );
                    setCalendarCursor(d);
                  }}
                >
                  {safeT("leave.form.calendarPrev", "Önceki")}
                </button>
                <button
                  className="ghost"
                  type="button"
                  onClick={() => {
                    const d = new Date();
                    setCalendarCursor(new Date(d.getFullYear(), d.getMonth(), 1));
                  }}
                >
                  {safeT("leave.form.calendarToday", "Bugün")}
                </button>
                <button
                  className="ghost"
                  type="button"
                  onClick={() => {
                    const d = new Date(
                      calendarCursor.getFullYear(),
                      calendarCursor.getMonth() + 1,
                      1
                    );
                    setCalendarCursor(d);
                  }}
                >
                  {safeT("leave.form.calendarNext", "Sonraki")}
                </button>
              </div>
            </div>

            <div className="range-calendar-months">
              {Array.from({ length: 2 }, (_, i) => {
                const monthDate = new Date(
                  calendarCursor.getFullYear(),
                  calendarCursor.getMonth() + i,
                  1
                );
                const monthKey = `${monthDate.getFullYear()}-${monthDate.getMonth()}`;
                const locale = lang === "tr" ? "tr-TR" : "en-US";
                const monthLabel = monthDate.toLocaleDateString(locale, {
                  month: "long",
                  year: "numeric",
                });
                const weekdays =
                  lang === "tr"
                    ? ["Pzt", "Sal", "Çar", "Per", "Cum", "Cmt", "Paz"]
                    : ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
                const weeks = monthMatrix(monthDate.getFullYear(), monthDate.getMonth());
                const maxEndKey = calculateMaxEndDate();

                const inRange = (key) => {
                  if (!key || !startDate || !endDate) return false;
                  const a = String(startDate).slice(0, 10);
                  const b = String(endDate).slice(0, 10);
                  const min = a <= b ? a : b;
                  const max = a <= b ? b : a;
                  return key >= min && key <= max;
                };

                const isSelectedStart = (key) => key && startDate && key === String(startDate).slice(0, 10);
                const isSelectedEnd = (key) => key && endDate && key === String(endDate).slice(0, 10);

                return (
                  <div className="range-calendar-month" key={monthKey}>
                    <div className="range-calendar-month-name">{monthLabel}</div>
                    <table className="range-calendar-table" aria-label={monthLabel}>
                      <thead>
                        <tr>
                          {weekdays.map((w) => (
                            <th key={w}>{w}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {weeks.map((week, wi) => (
                          <tr key={wi}>
                            {week.map((d, di) => {
                              if (!d) return <td key={di} className="empty" />;
                              const key = toLocalDateKey(d);
                              const holidayName = key ? calendarHolidayMap.get(key) : null;
                              const holiday = Boolean(holidayName);
                              const isTooOld = key && minStartKey && key < minStartKey;
                              const exceedsMaxEnd =
                                !isHourlyLeaveSelected && maxEndKey && key && key > maxEndKey;
                              const disabled = Boolean(isTooOld || exceedsMaxEnd);

                              const selectedCls = isSelectedStart(key)
                                ? "selected start"
                                : isSelectedEnd(key)
                                  ? "selected end"
                                  : "";
                              const inRangeCls = inRange(key) ? "in-range" : "";

                              return (
                                <td key={di} className="day">
                                  <button
                                    type="button"
                                    className={`day-btn ${selectedCls} ${inRangeCls} ${holiday ? "holiday" : ""}`}
                                    disabled={disabled}
                                    title={
                                      holidayName
                                        ? `${key} - ${holidayName}`
                                        : key || ""
                                    }
                                    onClick={() => {
                                      if (!key) return;
                                      if (isHourlyLeaveSelected || isHalfDaySelected) {
                                        setStartDate(key);
                                        setEndDate(key);
                                        return;
                                      }

                                      if (!startDate || (startDate && endDate)) {
                                        setStartDate(key);
                                        setEndDate("");
                                        return;
                                      }

                                      const startKey = String(startDate).slice(0, 10);
                                      if (key < startKey) {
                                        setStartDate(key);
                                        setEndDate("");
                                        return;
                                      }

                                      setEndDate(key);
                                    }}
                                  >
                                    <div className="num">{d.getDate()}</div>
                                    {holiday && (
                                      <div className="holiday-chip" title={holidayName || ""}>
                                        {holidayShortLabel(holidayName)}
                                      </div>
                                    )}
                                  </button>
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              })}
            </div>

            <div className="range-calendar-help">
              <span className="mono">{startDate ? String(startDate).slice(0, 10) : "—"}</span>
              <span className="sep">→</span>
              <span className="mono">{endDate ? String(endDate).slice(0, 10) : "—"}</span>
              {calendarHolidayMap.size > 0 && (
                <span className="hint">
                  {safeT("leave.form.calendarHolidayHint", "Resmi tatiller işaretlidir.")}
                </span>
              )}
            </div>
          </div>

          {publicHolidays.length > 0 && (
            <div className="holiday-hint">
              <div className="holiday-title">
                {safeT("leave.form.publicHolidays", "Resmi tatiller")}
              </div>
              <div className="holiday-subtitle">
                {safeT(
                  "leave.form.publicHolidaysNotice",
                  "Bu gunler izin dusumune dahil edilmez."
                )}
              </div>
              <ul>
                {publicHolidays.map((h) => {
                  const displayDate = normalizeDateKey(h?.date) || h?.date;
                  return (
                    <li key={`${h.date}-${h.name}`}>
                      <span className="mono">{displayDate}</span> - {h.name}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      )}

      {currentStepKey === "reason" && (
        <div className="step-panel">
          <label>{t("leave.form.reason")}:</label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t("leave.form.reasonPlaceholder")}
            required
          ></textarea>

          {isReportLeaveSelected && (
            <div style={{ marginTop: 12 }}>
              <label>{safeT("leave.form.reportPdfLabel", "Rapor (PDF) - opsiyonel")}:</label>
              <input
                className="leave-form input"
                type="file"
                accept="application/pdf,.pdf"
                onChange={(e) => {
                  const f = e.target.files?.[0] || null;
                  if (!f) {
                    setReportPdfFile(null);
                    return;
                  }
                  if (String(f.type || "").toLowerCase() !== "application/pdf") {
                    setReportPdfFile(null);
                    setNotification({
                      message: safeT("leave.form.reportPdfOnlyPdf", "Lütfen sadece PDF yükleyin."),
                      type: "error",
                    });
                    return;
                  }
                  if (f.size > 10 * 1024 * 1024) {
                    setReportPdfFile(null);
                    setNotification({
                      message: safeT("leave.form.reportPdfTooLarge", "PDF dosyası maksimum 10MB olmalıdır."),
                      type: "error",
                    });
                    return;
                  }
                  setReportPdfFile(f);
                }}
              />
              <div className="muted small">
                {reportPdfFile
                  ? `${safeT("leave.form.reportPdfSelected", "Seçilen dosya")}: ${reportPdfFile.name}`
                  : safeT("leave.form.reportPdfHint", "İstersen raporunu PDF olarak yükleyebilirsin.")}
              </div>
            </div>
          )}
        </div>
      )}

      {currentStepKey === "travelDetail" && (
        <div className="step-panel">
          <div>
            <label>{safeT("leave.form.travel.tripType", "Seyahat tipi")}:</label>
            <select
              className="leave-form select"
              value={travelTripType}
              onChange={(e) => setTravelTripType(e.target.value)}
            >
              <option value="domestic">{safeT("leave.form.travel.tripType.domestic", "Yurtiçi (TL)")}</option>
              <option value="international">
                {safeT("leave.form.travel.tripType.international", "Yurtdışı (EUR)")}
              </option>
              <option value="intracity">
                {safeT("leave.form.travel.tripType.intracity", "Şehir içi (harcırahsız)")}
              </option>
            </select>
          </div>

          <div>
            <label>{safeT("leave.form.travel.destination", "Seyahat edilen yer")}:</label>
            <input
              className="leave-form input"
              type="text"
              value={travelDestination}
              onChange={(e) => setTravelDestination(e.target.value)}
              maxLength={200}
              required
            />
          </div>

          <div>
            <label>{safeT("leave.form.travel.purpose", "Seyahat amacı (opsiyonel)")}:</label>
            <textarea
              value={travelPurpose}
              onChange={(e) => setTravelPurpose(e.target.value)}
              maxLength={1000}
            />
          </div>

          <div style={{ marginTop: 12 }}>
            <label className="checkbox-row">
              <input
                type="checkbox"
                checked={travelTransportRequired}
                onChange={(e) => {
                  setTravelTransportRequired(e.target.checked);
                  if (!e.target.checked) setTravelTransportType("");
                }}
              />
              <span>{safeT("leave.form.travel.transportRequired", "Ulaşım gerekli mi?")}</span>
            </label>

            {travelTransportRequired && (
              <div>
                <label>{safeT("leave.form.travel.transportType", "Ulaşım tipi")}:</label>
                <select
                  className="leave-form select"
                  value={travelTransportType}
                  onChange={(e) => setTravelTransportType(e.target.value)}
                  required={travelTransportRequired}
                >
                  <option value="">{safeT("leave.form.select", "Seçiniz")}</option>
                  <option value="plane">{safeT("leave.form.travel.transport.plane", "Uçak")}</option>
                  <option value="train">{safeT("leave.form.travel.transport.train", "Tren")}</option>
                  <option value="bus">{safeT("leave.form.travel.transport.bus", "Otobüs")}</option>
                  <option value="car_rental">
                    {safeT("leave.form.travel.transport.car_rental", "Araç Kiralama")}
                  </option>
                </select>
              </div>
            )}
          </div>

          <div style={{ marginTop: 12 }}>
            <label className="checkbox-row">
              <input
                type="checkbox"
                checked={travelAccommodationRequired}
                onChange={(e) => {
                  setTravelAccommodationRequired(e.target.checked);
                  if (!e.target.checked) setTravelAccommodationLink("");
                }}
              />
              <span>{safeT("leave.form.travel.accommodationRequired", "Konaklama gerekli mi?")}</span>
            </label>

            {travelAccommodationRequired && (
              <div>
                <label>{safeT("leave.form.travel.accommodationLink", "Otel/Konaklama linki")}:</label>
                <input
                  className="leave-form input"
                  type="url"
                  placeholder="https://..."
                  value={travelAccommodationLink}
                  onChange={(e) => setTravelAccommodationLink(e.target.value)}
                  maxLength={1000}
                  required={travelAccommodationRequired}
                />
              </div>
            )}
          </div>
        </div>
      )}

      {currentStepKey === "harcirah" && (
        <div className="step-panel">
          {(() => {
            const isIntracity = travelTripType === "intracity";
            const currency = travelTripType === "international" ? "EUR" : "TL";
            const daily = isIntracity
              ? 0
              : travelTripType === "international"
                ? travelDailyAmountEur
                : travelDailyAmountTry;
            const days = Number(totalCalculatedDays) || 0;
            const total = Math.round(daily * days * 100) / 100;
            return (
              <>
                <div className="muted small">
                  {safeT(
                    "leave.form.travel.harcirahHint",
                    "Günlük harcırah tutarı admin tarafından belirlenir; izin oluşturulduğu an forma snapshot olarak yapışır."
                  )}
                </div>
                <div style={{ marginTop: 12 }}>
                  <label>{safeT("leave.form.travel.tripDays", "Gün sayısı")}:</label>
                  <input className="leave-form input" type="text" value={`${days} gün`} readOnly />
                </div>
                <div>
                  <label>
                    {safeT("leave.form.travel.dailyAmount", "Günlük harcırah")} ({currency}):
                  </label>
                  <input
                    className="leave-form input"
                    type="text"
                    value={`${Number(daily).toLocaleString("tr-TR", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })} ${currency}`}
                    readOnly
                  />
                </div>
                <div>
                  <label>
                    <strong>{safeT("leave.form.travel.totalAmount", "Toplam harcırah")}:</strong>
                  </label>
                  <input
                    className="leave-form input"
                    type="text"
                    value={`${total.toLocaleString("tr-TR", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })} ${currency}`}
                    readOnly
                  />
                </div>
                {isIntracity && (
                  <div className="muted small" style={{ marginTop: 8 }}>
                    {safeT(
                      "leave.form.travel.intracityNoPerDiem",
                      "Şehir içi görevlerde harcırah ödenmez."
                    )}
                  </div>
                )}
                {!isIntracity && !(daily > 0) && (
                  <div className="warning-text">
                    {safeT(
                      "leave.form.travel.dailyAmountZero",
                      "Uyarı: Günlük harcırah tutarı 0. Admin'den ayarlanması gerekiyor."
                    )}
                  </div>
                )}
              </>
            );
          })()}
        </div>
      )}

      {currentStepKey === "ekHarcama" && (
        <div className="step-panel">
          <div className="muted small" style={{ marginBottom: 12 }}>
            {safeT(
              "leave.form.travel.ekHarcamaPrompt",
              "Ek harcama formu doldurmak ister misiniz? Opsiyoneldir."
            )}
          </div>
          <label className="checkbox-row">
            <input
              type="checkbox"
              checked={travelHasExtraExpenses}
              onChange={(e) => {
                setTravelHasExtraExpenses(e.target.checked);
                if (!e.target.checked) setTravelExpenseItems([]);
                else if (travelExpenseItems.length === 0) {
                  setTravelExpenseItems([
                    {
                      payee_company: "",
                      expense_date: "",
                      invoice_amount: 0,
                      limit_amount: "",
                      payable_amount: 0,
                      description: "",
                      receiptFile: null,
                    },
                  ]);
                }
              }}
            />
            <span>{safeT("leave.form.travel.hasExtraExpenses", "Evet, ek harcama gireceğim")}</span>
          </label>

          {travelHasExtraExpenses && (
            <div style={{ marginTop: 12 }}>
              {travelExpenseItems.map((it, idx) => (
                <div
                  key={idx}
                  className="card"
                  style={{ padding: 12, marginBottom: 10, border: "1px solid #cbd5e1" }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <strong>{`${safeT("leave.form.travel.item", "Kalem")} #${idx + 1}`}</strong>
                    <button
                      type="button"
                      className="ghost"
                      onClick={() =>
                        setTravelExpenseItems((prev) => prev.filter((_, i) => i !== idx))
                      }
                    >
                      {safeT("common.delete", "Sil")}
                    </button>
                  </div>
                  <div>
                    <label>{safeT("leave.form.travel.payeeCompany", "Ödeme yapılan şirket")}:</label>
                    <input
                      className="leave-form input"
                      type="text"
                      value={it.payee_company}
                      onChange={(e) =>
                        setTravelExpenseItems((prev) =>
                          prev.map((row, i) => (i === idx ? { ...row, payee_company: e.target.value } : row))
                        )
                      }
                      maxLength={200}
                    />
                  </div>
                  <div>
                    <label>{safeT("leave.form.travel.expenseDate", "Tarih")}:</label>
                    <input
                      className="leave-form input"
                      type="date"
                      value={it.expense_date}
                      onChange={(e) =>
                        setTravelExpenseItems((prev) =>
                          prev.map((row, i) => (i === idx ? { ...row, expense_date: e.target.value } : row))
                        )
                      }
                    />
                  </div>
                  <div>
                    <label>{safeT("leave.form.travel.invoiceAmount", "Fatura bedeli")}:</label>
                    <input
                      className="leave-form input"
                      type="number"
                      min="0"
                      step="0.01"
                      value={it.invoice_amount}
                      onChange={(e) =>
                        setTravelExpenseItems((prev) =>
                          prev.map((row, i) =>
                            i === idx ? { ...row, invoice_amount: e.target.value } : row
                          )
                        )
                      }
                    />
                  </div>
                  <div>
                    <label>{safeT("leave.form.travel.limitAmount", "Limit (opsiyonel)")}:</label>
                    <input
                      className="leave-form input"
                      type="number"
                      min="0"
                      step="0.01"
                      value={it.limit_amount}
                      onChange={(e) =>
                        setTravelExpenseItems((prev) =>
                          prev.map((row, i) =>
                            i === idx ? { ...row, limit_amount: e.target.value } : row
                          )
                        )
                      }
                    />
                  </div>
                  <div>
                    <label>{safeT("leave.form.travel.payableAmount", "Ödenecek bedel")}:</label>
                    <input
                      className="leave-form input"
                      type="number"
                      min="0"
                      step="0.01"
                      value={it.payable_amount}
                      onChange={(e) =>
                        setTravelExpenseItems((prev) =>
                          prev.map((row, i) =>
                            i === idx ? { ...row, payable_amount: e.target.value } : row
                          )
                        )
                      }
                    />
                  </div>
                  <div>
                    <label>{safeT("leave.form.travel.description", "Açıklama")}:</label>
                    <input
                      className="leave-form input"
                      type="text"
                      value={it.description}
                      onChange={(e) =>
                        setTravelExpenseItems((prev) =>
                          prev.map((row, i) =>
                            i === idx ? { ...row, description: e.target.value } : row
                          )
                        )
                      }
                      maxLength={1000}
                    />
                  </div>
                  <div>
                    <label>{safeT("leave.form.travel.receiptFile", "Fiş/Fatura PDF (opsiyonel)")}:</label>
                    <input
                      className="leave-form input"
                      type="file"
                      accept="application/pdf,.pdf"
                      onChange={(e) => {
                        const f = e.target.files?.[0] || null;
                        if (f) {
                          if (String(f.type || "").toLowerCase() !== "application/pdf") {
                            setNotification({
                              message: safeT(
                                "leave.form.reportPdfOnlyPdf",
                                "Lütfen sadece PDF yükleyin."
                              ),
                              type: "error",
                            });
                            return;
                          }
                          if (f.size > 10 * 1024 * 1024) {
                            setNotification({
                              message: safeT(
                                "leave.form.reportPdfTooLarge",
                                "PDF dosyası maksimum 10MB olmalıdır."
                              ),
                              type: "error",
                            });
                            return;
                          }
                        }
                        setTravelExpenseItems((prev) =>
                          prev.map((row, i) =>
                            i === idx ? { ...row, receiptFile: f } : row
                          )
                        );
                      }}
                    />
                    {it.receiptFile && (
                      <div className="muted small">{it.receiptFile.name}</div>
                    )}
                  </div>
                </div>
              ))}
              <button
                type="button"
                className="ghost"
                onClick={() =>
                  setTravelExpenseItems((prev) => [
                    ...prev,
                    {
                      payee_company: "",
                      expense_date: "",
                      invoice_amount: 0,
                      limit_amount: "",
                      payable_amount: 0,
                      description: "",
                      receiptFile: null,
                    },
                  ])
                }
              >
                + {safeT("leave.form.travel.addItem", "Kalem ekle")}
              </button>
            </div>
          )}
        </div>
      )}

      {currentStepKey === "review" && (
        <div className="step-panel review">
          <div className="review-grid">
            <div>
              <div className="review-k">{t("leave.form.leaveType")}</div>
              <div className="review-v">
                {selectedLeaveType?.name || "-"}
                {selectedSubLeaveType?.name ? ` (${selectedSubLeaveType.name})` : ""}
              </div>
            </div>
            <div>
              <div className="review-k">{t("leave.form.startDate")}</div>
              <div className="review-v">{startDate || "-"}</div>
            </div>
            {isHalfDaySelected && (
              <div>
                <div className="review-k">{safeT("leave.form.halfDayPeriod", "Yarım gün")}</div>
                <div className="review-v">
                  {halfDayPeriod === "AM"
                    ? safeT("leave.form.halfDayPeriod.am", "Öğleden önce")
                    : halfDayPeriod === "PM"
                      ? safeT("leave.form.halfDayPeriod.pm", "Öğleden sonra")
                      : "-"}
                </div>
              </div>
            )}
            {!isHourlyLeaveSelected && !isHalfDaySelected && (
              <div>
                <div className="review-k">{t("leave.form.endDate")}</div>
                <div className="review-v">{endDate || "-"}</div>
              </div>
            )}
            {isHourlyLeaveSelected && (
              <div>
                <div className="review-k">{t("leave.form.startTime")}</div>
                <div className="review-v">{startTime || "-"}</div>
              </div>
            )}
            {isHourlyLeaveSelected && (
              <div>
                <div className="review-k">{t("leave.form.endTime")}</div>
                <div className="review-v">{endTime || "-"}</div>
              </div>
            )}
            <div className="review-wide">
              <div className="review-k">{t("leave.form.reason")}</div>
              <div className="review-v">{reason || "-"}</div>
            </div>
            {isReportLeaveSelected && (
              <div className="review-wide">
                <div className="review-k">{safeT("leave.form.reportPdfLabelShort", "Rapor PDF")}</div>
                <div className="review-v">{reportPdfFile ? reportPdfFile.name : "-"}</div>
              </div>
            )}
            {isTravelLeaveSelected && (() => {
              const currency = travelTripType === "international" ? "EUR" : "TL";
              const daily =
                travelTripType === "intracity"
                  ? 0
                  : travelTripType === "international"
                    ? travelDailyAmountEur
                    : travelDailyAmountTry;
              const days = Number(totalCalculatedDays) || 0;
              const total = Math.round(daily * days * 100) / 100;
              const transportLabels = {
                plane: safeT("leave.form.travel.transport.plane", "Uçak"),
                train: safeT("leave.form.travel.transport.train", "Tren"),
                bus: safeT("leave.form.travel.transport.bus", "Otobüs"),
                car_rental: safeT("leave.form.travel.transport.car_rental", "Araç Kiralama"),
              };
              return (
                <>
                  <div className="review-wide">
                    <div className="review-k">
                      {safeT("leave.form.travel.tripType", "Seyahat tipi")}
                    </div>
                    <div className="review-v">
                      {travelTripType === "international"
                        ? safeT("leave.form.travel.tripType.international", "Yurtdışı (EUR)")
                        : travelTripType === "intracity"
                          ? safeT("leave.form.travel.tripType.intracity", "Şehir içi (harcırahsız)")
                          : safeT("leave.form.travel.tripType.domestic", "Yurtiçi (TL)")}
                    </div>
                  </div>
                  <div className="review-wide">
                    <div className="review-k">
                      {safeT("leave.form.travel.destination", "Seyahat edilen yer")}
                    </div>
                    <div className="review-v">{travelDestination || "-"}</div>
                  </div>
                  {travelPurpose && (
                    <div className="review-wide">
                      <div className="review-k">{safeT("leave.form.travel.purpose", "Amaç")}</div>
                      <div className="review-v">{travelPurpose}</div>
                    </div>
                  )}
                  <div>
                    <div className="review-k">{safeT("leave.form.travel.transport", "Ulaşım")}</div>
                    <div className="review-v">
                      {travelTransportRequired
                        ? transportLabels[travelTransportType] || "-"
                        : safeT("leave.form.travel.notNeeded", "Gerekli değil")}
                    </div>
                  </div>
                  <div>
                    <div className="review-k">
                      {safeT("leave.form.travel.accommodation", "Konaklama")}
                    </div>
                    <div className="review-v">
                      {travelAccommodationRequired
                        ? travelAccommodationLink || "-"
                        : safeT("leave.form.travel.notNeeded", "Gerekli değil")}
                    </div>
                  </div>
                  <div className="review-wide">
                    <div className="review-k">
                      {safeT("leave.form.travel.totalAmount", "Toplam harcırah")}
                    </div>
                    <div className="review-v">
                      {`${total.toLocaleString("tr-TR", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })} ${currency}`}{" "}
                      ({days} × {daily} {currency})
                    </div>
                  </div>
                  <div className="review-wide">
                    <div className="review-k">
                      {safeT("leave.form.travel.extraExpenses", "Ek harcamalar")}
                    </div>
                    <div className="review-v">
                      {travelHasExtraExpenses
                        ? `${travelExpenseItems.length} ${safeT(
                            "leave.form.travel.itemCountSuffix",
                            "kalem"
                          )}`
                        : safeT("leave.form.travel.noExtra", "Yok")}
                    </div>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}

      {shouldShowSummary && (
        <div className="leave-summary-box">
          <h3 className="leave-summary-box h3">{leaveSummaryTitle}</h3>

          {isAnnualLeaveSelected && (
            <p>
              <strong>{t("leave.summary.currentAnnualBalance")}:</strong>{" "}
              {annualLeaveBalance} {t("leave.summary.days")}
            </p>
          )}

          {isHourlyLeaveSelected ? (
            <>
              <p className="leave-summary-box p">
                <strong>{t("leave.summary.requestedDuration")}:</strong>{" "}
                {formatHoursToHHMM(totalCalculatedHours)}
              </p>
              <p className="leave-summary-box p">
                <strong>{t("leave.summary.returnTime")}:</strong>{" "}
                {returnToWorkTime ? returnToWorkTime : t("leave.form.calculating")}
              </p>
              <p className="leave-summary-box p">
                <strong>{t("leave.summary.returnDate")}:</strong>{" "}
                {startDate
                  ? new Date(startDate).toLocaleDateString(lang === "tr" ? "tr-TR" : "en-US", {
                      day: "2-digit",
                      month: "2-digit",
                      year: "numeric",
                    })
                  : t("leave.form.calculating")}
              </p>
            </>
          ) : (
            <>
              <p className="leave-summary-box p">
                <strong>{t("leave.summary.requestedDuration")}:</strong>{" "}
                {totalCalculatedDays} {t("leave.summary.businessDays")}
              </p>
              {isAnnualLeaveSelected && (
                <p className="leave-summary-box p">
                  <strong>{t("leave.summary.remainingAfter")}:</strong>{" "}
                  <span
                    style={{
                      color: remainingBalanceAfterLeave < 0 ? "red" : "green",
                    }}
                  >
                    {remainingBalanceAfterLeave} {t("leave.summary.days")}
                  </span>
                </p>
              )}
              <p className="leave-summary-box p">
                <strong>{t("leave.summary.returnDate")}:</strong>{" "}
                {returnToWorkDate
                  ? returnToWorkDate.toLocaleDateString(lang === "tr" ? "tr-TR" : "en-US", {
                      day: "2-digit",
                      month: "2-digit",
                      year: "numeric",
                    })
                  : t("leave.form.calculating")}
              </p>
            </>
          )}
        </div>
      )}

      <div className="wizard-actions">
        {step > 0 && (
          <button className="ghost" type="button" onClick={handleBack}>
            {t("leave.form.back")}
          </button>
        )}
        {step < steps.length - 1 ? (
          <button className="primary" type="button" onClick={handleNext}>
            {t("leave.form.next")}
          </button>
        ) : (
          <button className="primary" type="submit" disabled={submitting}>
            {t("leave.form.confirm")}
          </button>
        )}
      </div>
    </form>
  );
};

export default LeaveForm;
